import React, { useEffect, useMemo, useState } from "react";
import { Table, Select, Tooltip, Tag, Button, } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { getLeads, updateLeadStatus } from "../features/customers/customerSlice";
import { WhatsAppOutlined, PhoneOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import FacebookAgentsLeads from "../components/FacebookAgentLeads";
import "../styles/leads.css";
 import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";


const lostStatuses = [
  "bought_elsewhere",
  "not_interested",
  "no_budget",
  "no_response",
  "spam",
];

const statusOptions = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "follow_up", label: "Follow Up" },
  { value: "appointment_booked", label: "Appointment Booked" },
  { value: "deal_closed", label: "Deal Closed" },
  { value: "bought_elsewhere", label: "Lost - Bought Elsewhere" },
  { value: "not_interested", label: "Lost - Not Interested" },
  { value: "no_budget", label: "Lost - No Budget" },
  { value: "no_response", label: "Lost - No Response" },
  { value: "spam", label: "Lost - Spam" },
];

const statusLabelMap = {
  new: "New",
  contacted: "Contacted",
  follow_up: "Follow Up",
  appointment_booked: "Appointment Booked",
  deal_closed: "Deal Closed",
  bought_elsewhere: "Lost - Bought Elsewhere",
  not_interested: "Lost - Not Interested",
  no_budget: "Lost - No Budget",
  no_response: "Lost - No Response",
  spam: "Lost - Spam",
};

const MyLeadsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isDesktop = useMediaQuery({ minWidth: 785 });

  const [selectedMonth, setSelectedMonth] = useState("all");
  const [selectedWeek, setSelectedWeek] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [isCompactView, setIsCompactView] = useState(window.innerWidth <= 789);
  const [activeLeadView, setActiveLeadView] = useState("whatsapp");

  const { leads: leadState = [], isLoading } = useSelector(
    (state) => state.customer
  );

  const [hasFetchedLeads, setHasFetchedLeads] = useState(
    Array.isArray(leadState) && leadState.length > 0
  );

  const storedUser = localStorage.getItem("user")
    ? JSON.parse(localStorage.getItem("user"))
    : null;

  const agentName = storedUser?.agentName;

  useEffect(() => {
    if (Array.isArray(leadState) && leadState.length > 0) {
      setHasFetchedLeads(true);
      return;
    }

    const fetchLeads = async () => {
      try {
        await dispatch(getLeads()).unwrap();
      } catch (error) {
        console.error("Failed to fetch leads:", error);
      } finally {
        setHasFetchedLeads(true);
      }
    };

    fetchLeads();
  }, [dispatch, leadState]);

  useEffect(() => {
    const handleResize = () => {
      setIsCompactView(window.innerWidth <= 789);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const openChat = (phone) => {
    if (!phone) return;
    navigate(`/admin/agent-whatsapp/${encodeURIComponent(phone)}`);
  };

  const getWeekOfMonth = (dateValue) => {
    const date = new Date(dateValue);
    return Math.ceil(date.getDate() / 7);
  };

  const monthOptions = [
    { value: "all", label: "All Months" },
    { value: 0, label: "January" },
    { value: 1, label: "February" },
    { value: 2, label: "March" },
    { value: 3, label: "April" },
    { value: 4, label: "May" },
    { value: 5, label: "June" },
    { value: 6, label: "July" },
    { value: 7, label: "August" },
    { value: 8, label: "September" },
    { value: 9, label: "October" },
    { value: 10, label: "November" },
    { value: 11, label: "December" },
  ];

  const weekOptions = [
    { value: "all", label: "All Weeks" },
    { value: 1, label: "Week 1" },
    { value: 2, label: "Week 2" },
    { value: 3, label: "Week 3" },
    { value: 4, label: "Week 4" },
    { value: 5, label: "Week 5" },
  ];

  const myLeads = useMemo(() => {
    return leadState.filter((lead) => lead.agentName === agentName);
  }, [leadState, agentName]);

  const filteredLeads = useMemo(() => {
    return myLeads.filter((lead) => {
      const createdAt = lead.createdAt ? new Date(lead.createdAt) : null;
      if (!createdAt || Number.isNaN(createdAt.getTime())) return false;

      let matchesMonth = true;
      let matchesWeek = true;
      let matchesStatus = true;

      if (selectedMonth !== "all") {
        matchesMonth = createdAt.getMonth() === selectedMonth;
      }

      if (selectedWeek !== "all") {
        matchesWeek = getWeekOfMonth(lead.createdAt) === selectedWeek;
      }

      if (selectedStatus) {
        matchesStatus =
          lead.status === selectedStatus ||
          (selectedStatus === "lost" &&
            (lead.status === "lost" || lostStatuses.includes(lead.status)));
      }

      return matchesMonth && matchesWeek && matchesStatus;
    });
  }, [myLeads, selectedMonth, selectedWeek, selectedStatus]);

  const totalLeads = filteredLeads.length;
  const contacted = filteredLeads.filter((lead) => lead.status !== "new").length;
  const followUps = filteredLeads.filter(
    (lead) => lead.status === "follow_up"
  ).length;
  const appointmentBooked = filteredLeads.filter(
    (lead) => lead.status === "appointment_booked"
  ).length;
  const dealsClosed = filteredLeads.filter(
    (lead) => lead.status === "deal_closed"
  ).length;
  const dealsLost = filteredLeads.filter(
    (lead) => lead.status === "lost" || lostStatuses.includes(lead.status)
  ).length;

  const tableData = filteredLeads.map((lead, index) => ({
    key: lead._id,
    index: index + 1,
    name: lead.name || "Not Provided",
    phone: lead.phone,
    message: lead.message || "No message",
    status: lead.status,
    date: new Date(lead.createdAt).toLocaleDateString(),
  }));

  const leadsLoading =
    !hasFetchedLeads ||
    (isLoading && (!Array.isArray(leadState) || leadState.length === 0));

  const renderStatusSelect = (record, fullWidth = false) => (
    <Select
      value={record.status}
      className={fullWidth ? "lead-status-select w-100" : "lead-status-select"}
      style={{ width: "100%" }}
      onChange={(value) => {
        dispatch(
          updateLeadStatus({
            id: record.key,
            status: value,
          })
        );
      }}
      options={statusOptions}
    />
  );

const renderKpiSkeletons = () => (
  <div className="leads__count-wrapper g-3 mb-4">
    {Array.from({ length: 6 }).map((_, index) => (
      <div className="col-xl-3 col-lg-3 col-md-6 col-12" key={index}>
        <div className="bg-white p-3 rounded-3 shadow-sm">
          <Skeleton height={12} width="50%" style={{ marginBottom: 10 }} />
          <Skeleton height={22} width="40%" />
        </div>
      </div>
    ))}
  </div>
);

const renderDesktopTableSkeleton = () => (
  <div className="bg-white p-3 p-md-4 leads-main-wrapper rounded-3">
    {Array.from({ length: 6 }).map((_, rowIndex) => (
      <div
        key={rowIndex}
        style={{
          display: "grid",
          gridTemplateColumns: "60px 1.2fr 2fr 1fr 120px 100px",
          gap: 12,
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <Skeleton height={12} />
        <Skeleton height={12} />
        <Skeleton height={12} />
        <Skeleton height={12} />
        <Skeleton height={12} />
        <Skeleton height={12} />
      </div>
    ))}
  </div>
);

 const renderMobileCardsSkeleton = () => (
  <div className="bg-white p-3 p-md-4 leads-main-wrapper rounded-3">
    <div className="d-flex flex-column gap-3">
      {Array.from({ length: 4 }).map((_, index) => (
        <div
          key={index}
          className="lead-mobile-card border rounded-4 p-3 shadow-sm bg-white"
        >
          {/* Top row */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Skeleton width={80} height={12} />
            <Skeleton width={90} height={18} />
          </div>

          {/* Name + phone */}
          <div className="mb-3">
            <Skeleton width="60%" height={14} style={{ marginBottom: 6 }} />
            <Skeleton width="50%" height={12} />
          </div>

          {/* Message */}
          <div className="mb-3">
            <Skeleton width={70} height={10} style={{ marginBottom: 6 }} />
            <Skeleton width="100%" height={12} />
          </div>

          {/* Status */}
          <div className="mb-3">
            <Skeleton width={70} height={10} style={{ marginBottom: 6 }} />
            <Skeleton width="100%" height={30} />
          </div>

          {/* Actions */}
          <div className="d-flex justify-content-end gap-3">
            <Skeleton circle width={32} height={32} />
            <Skeleton circle width={32} height={32} />
          </div>
        </div>
      ))}
    </div>
  </div>
);
  if (activeLeadView === "facebook") {
    return (
      <div>
        <FacebookAgentsLeads setActiveLeadView={setActiveLeadView} />
      </div>
    );
  }

  return (
    <div className="my__leads-wrapper">
      {isDesktop && (
        <div className="w-50 mb-3">
          <h3 className="mb-1">Whatsapp Leads</h3>
        </div>
      )}

      <div className="d-flex flex-column justify-content-lg-between flex-lg-row align-items-start align-items-lg-center gap-3 mb-4">
        <div className="w-100 d-flex gap-2 flex-wrap">
          <div className="leads-nav">
            <Button
              className={`leads-nav__item leads-nav__item--whatsapp ${
                activeLeadView === "whatsapp" ? "leads-nav__item--active" : ""
              }`}
              onClick={() => setActiveLeadView("whatsapp")}
            >
              WhatsApp Leads
            </Button>

            <Button
              className={`leads-nav__item leads-nav__item--facebook ${
                activeLeadView === "facebook" ? "leads-nav__item--active" : ""
              }`}
              onClick={() => setActiveLeadView("facebook")}
            >
              Facebook Leads
            </Button>
          </div>
        </div>

        <div className="whatsapp__sort-wrapper">
          <Select
            value={selectedMonth}
            onChange={(value) => {
              setSelectedMonth(value);
              setSelectedWeek("all");
            }}
            options={monthOptions}
          />

          <Select
            value={selectedWeek}
            onChange={(value) => setSelectedWeek(value)}
            options={weekOptions}
          />

          <Select
            placeholder="Filter by Status"
            value={selectedStatus}
            allowClear
            onChange={(value) => setSelectedStatus(value)}
            options={[
              { value: "new", label: "New" },
              { value: "contacted", label: "Contacted" },
              { value: "follow_up", label: "Follow Up" },
              { value: "appointment_booked", label: "Appointment Booked" },
              { value: "deal_closed", label: "Deal Closed" },
              { value: "lost", label: "Lost" },
            ]}
          />
        </div>
      </div>

      {leadsLoading ? (
        renderKpiSkeletons()
      ) : (
        <div className="leads__count-wrapper g-3 mb-4">
          <div className="col-xl-3 col-lg-3 col-md-6 col-12">
            <div
              className="bg-white p-3 rounded-3 shadow-sm"
              style={{ borderLeft: "5px solid #4f46e5" }}
            >
              <small style={{ color: "#4f46e5" }}>Total Leads</small>
              <h4 className="mb-0" style={{ color: "#1e1b4b" }}>
                {totalLeads}
              </h4>
            </div>
          </div>

          <div className="col-xl-3 col-lg-3 col-md-6 col-12">
            <div
              className="bg-white p-3 rounded-3 shadow-sm"
              style={{ borderLeft: "5px solid #0891b2" }}
            >
              <small style={{ color: "#0891b2" }}>Contacted</small>
              <h4 className="mb-0" style={{ color: "#164e63" }}>
                {contacted}
              </h4>
            </div>
          </div>

          <div className="col-xl-3 col-lg-3 col-md-6 col-12">
            <div
              className="bg-white p-3 rounded-3 shadow-sm"
              style={{ borderLeft: "5px solid #ea580c" }}
            >
              <small style={{ color: "#ea580c" }}>Follow Ups</small>
              <h4 className="mb-0" style={{ color: "#7c2d12" }}>
                {followUps}
              </h4>
            </div>
          </div>

          <div className="col-xl-3 col-lg-3 col-md-6 col-12">
            <div
              className="bg-white p-3 rounded-3 shadow-sm"
              style={{ borderLeft: "5px solid #7c3aed" }}
            >
              <small style={{ color: "#7c3aed" }}>Appointments</small>
              <h4 className="mb-0" style={{ color: "#4c1d95" }}>
                {appointmentBooked}
              </h4>
            </div>
          </div>

          <div className="col-xl-3 col-lg-3 col-md-6 col-12">
            <div
              className="bg-white p-3 rounded-3 shadow-sm"
              style={{ borderLeft: "5px solid #16a34a" }}
            >
              <small style={{ color: "#16a34a" }}>Deals Closed</small>
              <h4 className="mb-0" style={{ color: "#14532d" }}>
                {dealsClosed}
              </h4>
            </div>
          </div>

          <div className="col-xl-3 col-lg-3 col-md-6 col-12">
            <div
              className="bg-white p-3 rounded-3 shadow-sm"
              style={{ borderLeft: "5px solid #dc2626" }}
            >
              <small style={{ color: "#dc2626" }}>Deals Lost</small>
              <h4 className="mb-0" style={{ color: "#7f1d1d" }}>
                {dealsLost}
              </h4>
            </div>
          </div>
        </div>
      )}

      {leadsLoading ? (
        isCompactView ? renderMobileCardsSkeleton() : renderDesktopTableSkeleton()
      ) : (
        <div className="bg-white p-3 p-md-4 leads-main-wrapper rounded-3">
          {!isCompactView ? (
            <Table
              dataSource={tableData}
              pagination={{ pageSize: 10 }}
              columns={[
                {
                  title: "SNo",
                  dataIndex: "index",
                  width: 70,
                },
                {
                  title: "Name",
                  dataIndex: "name",
                },
                {
                  title: "Message",
                  dataIndex: "message",
                  ellipsis: true,
                },
                {
                  title: "Status",
                  render: (_, record) => renderStatusSelect(record),
                },
                {
                  title: "Date",
                  dataIndex: "date",
                },
                {
                  title: "Actions",
                  render: (_, record) => {
                    const phone = record.phone?.replace(/\D/g, "");
                    const callLink = `tel:${phone}`;

                    return (
                      <div className="d-flex gap-3">
                        <Tooltip title="Open Chat">
                          <span
                            onClick={() => openChat(record.phone)}
                            style={{ cursor: "pointer" }}
                          >
                            <WhatsAppOutlined
                              style={{ color: "green", fontSize: 18 }}
                            />
                          </span>
                        </Tooltip>

                        <Tooltip title="Call">
                          <a href={callLink}>
                            <PhoneOutlined
                              style={{ color: "#1890ff", fontSize: 18 }}
                            />
                          </a>
                        </Tooltip>
                      </div>
                    );
                  },
                },
              ]}
            />
          ) : (
            <div className="d-flex flex-column gap-3">
              {tableData.length > 0 ? (
                tableData.map((record) => {
                  const phone = record.phone?.replace(/\D/g, "");
                  const callLink = `tel:${phone}`;

                  return (
                    <div
                      key={record.key}
                      className="lead-mobile-card border rounded-4 p-3 shadow-sm bg-white"
                    >
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <small className="text-muted">{record.date}</small>
                        <Tag color="blue" className="mb-0">
                          {statusLabelMap[record.status]}
                        </Tag>
                      </div>

                      <div className="d-flex flex-column mb-3">
                        <h6 className="mb-1 fw-semibold">{record.name}</h6>

                        <div className="d-flex align-items-center gap-2">
                          <small className="text-muted">Phone:</small>
                          <span>{record.phone || "Not Provided"}</span>
                        </div>
                      </div>

                      <div className="mb-3">
                        <small className="text-muted d-block mb-1">Message</small>
                        <div className="lead-mobile-message">
                          {record.message || "No message"}
                        </div>
                      </div>

                      <div className="mb-3">
                        <small className="text-muted d-block mb-1">Status</small>
                        {renderStatusSelect(record, true)}
                      </div>

                      <div className="d-flex justify-content-end gap-3 pt-1">
                        <Tooltip title="Open Chat">
                          <span
                            onClick={() => openChat(record.phone)}
                            className="lead-mobile-action whatsapp"
                            style={{ cursor: "pointer" }}
                          >
                            <WhatsAppOutlined style={{ fontSize: 20 }} />
                          </span>
                        </Tooltip>

                        <Tooltip title="Call">
                          <a href={callLink} className="lead-mobile-action call">
                            <PhoneOutlined style={{ fontSize: 20 }} />
                          </a>
                        </Tooltip>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-4 text-muted">No leads found</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MyLeadsPage;